import DocumentsPanel from "./DocumentsPanel.jsx";
import ShareholdingPattern from "./ShareholdingPattern.jsx";
import React, { useState, useEffect } from "react";
import { getValuationData, getInsiders } from "../../utils/companyApi.js";
import { loadUserData, saveUserData } from "../../utils/userStore.js";
import { getToken } from "../../utils/session.js";
import { ExternalLink, Check, X } from "lucide-react";
import LightChart from "../LightChart.jsx";
import MiniStatement from "./MiniStatement.jsx";
import useAsync from "../../utils/useAsync.js";
import {
  getKeyStats, getNews, getProfile, getQuote, getSnapshot, getProsCons,
  getValuation, getOwnership, getFilings,
} from "../../utils/stockApi.js";
import { fmtCell, fmtShortDate, fmtMoney, fmtPct, fmtNumber } from "../../utils/format.js";
import { stockPath } from "../../utils/route.js";
import { Loading, ErrorBox } from "./common.jsx";
import QuarterlyTable from "./QuarterlyTable.jsx";
import useLiveQuote from "../../utils/useLiveQuote.js";

function Section({ title, ticker, tab, children }) {
  return (
    <section className="co-section">
      <div className="co-section-head">
        <h5>{title}</h5>
        {tab && <a href={stockPath(ticker, tab)}>View full →</a>}
      </div>
      <div className="card co-card">{children}</div>
    </section>
  );
}

function StatBlock({ block }) {
  return (
    <div className="card co-card">
      <h6 className="co-card-title">{block.title}</h6>
      <dl className="co-stats">
        {block.items.map(([label, value, kind]) => (
          <div key={label}><dt>{label}</dt><dd>{fmtCell(value, kind, "MM")}</dd></div>
        ))}
      </dl>
    </div>
  );
}

// ---- 1. top card: price, links, ratio grid, about --------------------------
function HeaderCard({ ticker, profile, quote, snapshot }) {
  const [more, setMore] = useState(false);
  const p = profile;
  const q = quote;
  const up = q && q.change >= 0;
  return (
    <div className="card co-card co-top-card">
      <div className="co-top-left">
        {p ? (
          <>
            {q ? (
              <div className="co-top-price">
                <b>{fmtMoney(q.price)}</b>
                <span className={up ? "change-up" : "change-down"}>{fmtPct(q.changePct, 2, true)}</span>
                <small>Close · {fmtShortDate(q.date)}</small>
              </div>
            ) : (
              <div className="co-top-price"><small>No price data loaded yet</small></div>
            )}
            <div className="co-links">
              <a href={p.tvUrl} target="_blank" rel="noreferrer"><ExternalLink size={13} /> {p.exchange}: {p.ticker}</a>
              <a href={`https://www.sec.gov/cgi-bin/browse-edgar?action=getcompany&CIK=${encodeURIComponent(p.ticker)}&owner=include&count=40`} target="_blank" rel="noreferrer"><ExternalLink size={13} /> SEC filings</a>
              <a href={stockPath(ticker, "financials")}>Financials</a>
            </div>
          </>
        ) : <Loading />}
        <Ratios ticker={ticker} snapshot={snapshot} />
      </div>

      <div className="co-top-right">
        <h6 className="co-section-label">About</h6>
        {p ? (
          <>
            <AboutPoints text={p.description} ticker={ticker} />
            
            <Facts p={p} ticker={ticker} />
          </>
        ) : <Loading />}
      </div>
    </div>
  );
}

function ProsCons({ ticker }) {
  const { data } = useAsync(() => getProsCons(ticker), [ticker]);
  if (!data) return <Loading />;
  return (
    <div className="co-proscons">
      <div className="card co-card co-pros">
        <h6 className="co-card-title">Pros</h6>
        <ul>{data.pros.map((t) => <li key={t}><Check size={15} />{t}</li>)}</ul>
      </div>
      <div className="card co-card co-cons">
        <h6 className="co-card-title">Cons</h6>
        <ul>{data.cons.map((t) => <li key={t}><X size={15} />{t}</li>)}</ul>
      </div>
    </div>
  );
}

function OldPeers({ ticker }) {
  const { data, loading, error } = useAsync(() => getValuation(ticker), [ticker]);
  if (loading) return <Loading />;
  if (error) return <ErrorBox error={error} />;
  return (
    <div className="co-table-wrap">
      <table className="co-table co-table-left">
        <thead>
          <tr><th>Company</th><th>Price</th><th>Mkt Cap ($B)</th><th>P/E</th><th>Sales Growth</th><th>Net Margin</th><th>ROCE</th></tr>
        </thead>
        <tbody>
          {data.competitors.slice(0, 6).map((c) => (
            <tr key={c.ticker} className={c.self ? "selected" : ""}>
              <td><a className="wl-company" href={stockPath(c.ticker)}>{c.name}</a><small className="co-tk">{c.ticker}</small></td>
              <td>{fmtMoney(c.price)}</td>
              <td>{fmtCell(c.mcap, "count")}</td>
              <td>{fmtCell(c.pe, "x")}</td>
              <td>{fmtPct(c.salesVar, 1)}</td>
              <td>{fmtPct(c.margin, 1)}</td>
              <td>{fmtPct(c.roce, 1)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function OldHolders({ ticker }) {
  const { data, loading } = useAsync(() => getOwnership(ticker), [ticker]);
  if (loading) return <Loading />;
  const top = data.holders.slice(0, 5);
  const inst = data.holders.reduce((s, h) => s + h.pct, 0);
  return (
    <>
      <p className="co-hint">Top holders own {fmtPct(top.reduce((s, h) => s + h.pct, 0), 1)} of shares; all listed institutions together hold {fmtPct(inst, 1)}.</p>
      <div className="co-table-wrap">
        <table className="co-table co-table-left">
          <thead><tr><th>Holder</th><th>Type</th><th>% Out.</th><th>Change</th></tr></thead>
          <tbody>
            {top.map((h) => (
              <tr key={h.name}>
                <td>{h.name}</td><td>{h.type}</td><td>{fmtPct(h.pct, 2)}</td>
                <td className={h.change >= 0 ? "change-up" : "change-down"}>{fmtPct(h.change, 2, true)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function Documents({ ticker }) {
  const { data, loading } = useAsync(() => getFilings(ticker), [ticker]);
  if (loading) return <Loading />;
  return (
    <div className="co-table-wrap">
      <table className="co-table co-table-left">
        <thead><tr><th>Date</th><th>Form</th><th>Description</th><th /></tr></thead>
        <tbody>
          {data.slice(0, 6).map((f, i) => (
            <tr key={i}>
              <td>{fmtShortDate(f.date)}</td>
              <td><span className="badge-purple">{f.form}</span></td>
              <td>{f.description}</td>
              <td><a href={f.url} target="_blank" rel="noreferrer">EDGAR <ExternalLink size={12} /></a></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function OverviewTab({ ticker, showChart }) {
  const profile = useAsync(() => getProfile(ticker), [ticker]);
  const quote = useLiveQuote(ticker);
  const snapshot = useAsync(() => getSnapshot(ticker), [ticker]);
  const stats = useAsync(() => getKeyStats(ticker), [ticker]);
  const news = useAsync(() => getNews(ticker), [ticker]);
  const p = profile.data;

  return (
    <div className="co-overview">
      <HeaderCard ticker={ticker} profile={p} quote={quote.data} snapshot={snapshot.data} />

      {/* 2. chart (50%) | pros + cons (50%) */}
      <div className={`co-chart-row ${showChart ? "" : "no-chart"}`}>
        {showChart && p && (
          <div className="card co-card co-chart-card">
            <h6 className="co-card-title">
              <span>Price Chart</span>
              <a className="btn tool-btn tv-link" href={p.tvUrl} target="_blank" rel="noreferrer"
                title={`Open ${p.ticker} on TradingView`} aria-label={`Open ${p.ticker} on TradingView`}>
                <ExternalLink size={16} />
              </a>
            </h6>
            <div className="tv-chart-box"><LightChart ticker={ticker} /></div>
          </div>
        )}
        <ProsCons ticker={ticker} />
      </div>

      {/* 3. all the details, organised in sections */}
      <section className="co-section">
        <div className="co-section-head"><h5>Key Statistics</h5></div>
        {stats.loading ? <Loading /> : stats.error ? <ErrorBox error={stats.error} /> : (
          <div className="co-stat-grid">{stats.data.map((b) => <StatBlock key={b.title} block={b} />)}</div>
        )}
      </section>

      {/* hidden for now: <Section title="Peer Comparison" ticker={ticker} tab="valuation"><Peers ticker={ticker} /></Section> */}

      <Section title="Quarterly Results" ticker={ticker} tab="financials">
        <QuarterlyTable ticker={ticker} count={13} />
      </Section>

      <Section title="Profit & Loss" ticker={ticker} tab="financials">
        <MiniStatement ticker={ticker} statement="income" period="annual" cols={10}
          keys={["revenue", "revG", "gp", "ebitda", "opInc", "opm", "ni", "nim", "eps", "epsG"]} />
      </Section>

      <Section title="Balance Sheet" ticker={ticker} tab="financials">
        <MiniStatement ticker={ticker} statement="balance" period="annual" cols={10}
          keys={["cash", "tca", "ppe", "ta", "tcl", "debt", "tl", "equity"]} />
      </Section>

      <Section title="Cash Flow" ticker={ticker} tab="financials">
        <MiniStatement ticker={ticker} statement="cashflow" period="annual" cols={10}
          keys={["cfo", "capexNeg", "cfi", "cff", "netChange", "fcf"]} />
      </Section>

      <Section title="Ratios" ticker={ticker} tab="financials">
        <MiniStatement ticker={ticker} statement="ratios" period="annual" cols={10}
          keys={["gm", "opm", "nim", "roe", "roa", "roic", "currentRatio", "debtEquity", "interestCover"]} />
      </Section>

      <Section title="Shareholding" ticker={ticker} tab="ownership"><Holders ticker={ticker} /></Section>

      <Section title="Documents" ticker={ticker} tab="filings"><DocumentsPanel ticker={ticker} /></Section>

      <Section title="News">
        {news.data ? (
          <ul className="co-news">
            {news.data.map((n) => (
              <li key={n.title}>
                {n.url ? <a href={n.url} target="_blank" rel="noreferrer">{n.title}</a> : <span>{n.title}</span>}
                <small>{n.source} · {fmtShortDate(n.date)}</small>
              </li>
            ))}
          </ul>
        ) : <Loading />}
      </Section>
    </div>
  );
}



const dash = (v, f) => (v === null || v === undefined ? "—" : f(v));

function Peers({ ticker }) {
  const { data, loading, error } = useAsync(() => getValuationData(ticker), [ticker]);
  if (loading) return <Loading />;
  if (error || !data || data.error) return <p className="co-hint">No peer data yet. This company needs a price and SEC financials.</p>;
  const rows = [{ ...data.self, self: true }, ...data.peers].slice(0, 6);
  return (
    <div className="co-table-wrap">
      <table className="co-table co-table-left">
        <thead>
          <tr><th>Company</th><th>Price</th><th>Mkt Cap ($B)</th><th>P/E</th><th>Sales Growth</th><th>Net Margin</th><th>ROCE</th></tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.ticker} className={c.self ? "selected" : ""}>
              <td><a className="wl-company" href={stockPath(c.ticker)}>{c.name}</a><small className="co-tk">{c.ticker}</small></td>
              <td>{dash(c.price, fmtMoney)}</td>
              <td>{dash(c.mcap, (v) => fmtNumber(v, 1))}</td>
              <td>{fmtCell(c.pe, "x")}</td>
              <td>{dash(c.sales_g, (v) => fmtPct(v, 1))}</td>
              <td>{dash(c.nim, (v) => fmtPct(v, 1))}</td>
              <td>{dash(c.roce, (v) => fmtPct(v, 1))}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="co-hint">Closest in size in the same {data.groupType}.</p>
    </div>
  );
}

function HoldersOld({ ticker }) {
  const [st, setSt] = useState({ data: null, loading: true });
  useEffect(() => {
    let dead = false, timer = null;
    setSt({ data: null, loading: true });
    const load = () => getInsiders(ticker).then((d) => {
      if (dead) return;
      setSt({ data: d, loading: false });
      if (d && d.syncing) timer = setTimeout(load, 3000);
    }).catch(() => !dead && setSt({ data: null, loading: false }));
    load();
    return () => { dead = true; clearTimeout(timer); };
  }, [ticker]);
  if (st.loading) return <Loading />;
  const d = st.data;
  if (!d || d.error) return <p className="co-hint">No insider data for this company.</p>;
  const top = d.holdings.slice(0, 5);
  const so = d.sharesOutstandingMM ? d.sharesOutstandingMM * 1e6 : null;
  return (
    <>
      {d.syncing && <p className="co-hint">Reading insider filings from the SEC… about 10 seconds the first time.</p>}
      {!d.syncing && !top.length && <p className="co-hint">No insider filings (Form 4) in the last 13 months.</p>}
      {top.length > 0 && (
        <div className="co-table-wrap">
          <table className="co-table co-table-left">
            <thead><tr><th>Insider</th><th>Role</th><th>Shares</th><th>% Out.</th></tr></thead>
            <tbody>
              {top.map((h, i) => (
                <tr key={i}>
                  <td>{h.insider}</td><td>{h.role || "—"}</td><td>{fmtNumber(h.shares, 0)}</td>
                  <td>{so && /common|ordinary/i.test(h.security) ? fmtPct((h.shares / so) * 100, 3) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="co-hint">Insiders only (SEC Form 4). Fund holdings are not available yet.</p>
        </div>
      )}
    </>
  );
}







// Company description as bullet points (one per sentence) in a scrollable box.
function AboutPointsOld({ text }) {
  const safe = String(text || "")
    .replace(/\b(Inc|Corp|Co|Ltd|Jr|Sr|St|No|vs|Mr|Dr|Ms|Mrs|etc)\./g, "$1\u0000")
    .replace(/\b([A-Z])\./g, "$1\u0000");
  const points = [];
  for (const para of safe.split(/\n+/)) {
    const parts = para.match(/[^.!?]+(?:[.!?]+["')\]]*(?=\s|$)|$)/g) || [];
    for (const s of parts) {
      const t = s.replace(/\u0000/g, ".").replace(/\s+/g, " ").trim();
      if (t.length > 2) points.push(t);
    }
  }
  return (
    <div style={{ maxHeight: 230, overflowY: "auto", paddingRight: 8, marginBottom: 12 }}>
      <ul style={{ margin: 0, paddingLeft: "1.1rem" }}>
        {points.map((t, i) => <li key={i} style={{ marginBottom: 6 }}>{t}</li>)}
      </ul>
    </div>
  );
}
function splitPoints(text) {
  const safe = String(text || "")
    .replace(/\b(Inc|Corp|Co|Ltd|Jr|Sr|St|No|vs|Mr|Dr|Ms|Mrs|etc)\./g, "$1\u0000")
    .replace(/\b([A-Z])\./g, "$1\u0000");
  const out = [];
  for (const para of safe.split(/\n+/)) {
    for (const s of para.match(/[^.!?]+(?:[.!?]+["')\]]*(?=\s|$)|$)/g) || []) {
      const t = s.replace(/\u0000/g, ".").replace(/\s+/g, " ").trim();
      if (t.length > 2) out.push(t);
    }
  }
  return out;
}

// Company description as bullet points, one visible at a time (scroll for more).
function AboutPoints({ text }) {
  const points = splitPoints(text);
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ height: 100, overflowY: "auto", scrollSnapType: "y mandatory", paddingRight: 8 }}>
        <ul style={{ margin: 0, paddingLeft: "1.1rem" }}>
          {points.map((t, i) => <li key={i} style={{ marginBottom: 6, scrollSnapAlign: "start" }}>{t}</li>)}
        </ul>
      </div>
      {points.length > 1 && <small className="text-muted d-block mt-1">{points.length} points · scroll for more</small>}
    </div>
  );
}
// Company facts (read-only).
function Facts({ p }) {
  const clean = (v) => (v !== null && v !== undefined && /[A-Za-z0-9]/.test(String(v)) ? String(v) : "");
  const rows = [
    ["Sector", clean(p.sector)],
    ["Industry", clean(p.industry)],
    ["Country", clean(p.country)],
    ["Headquarters", clean(p.hq)],
    ["SEC industry", clean(p.sicDescription)],
    ["Fiscal year ends", clean(p.fiscalYearEnd)],
    ["Phone", clean(p.phone)],
    ["Website", clean(p.website)],
    ["Employees", p.employees ? Number(p.employees).toLocaleString("en-US") : ""],
  ];
  return (
    <dl className="co-stats co-overview-facts">
      {rows.map(([label, val]) => (val || label === "Employees") ? (
        <div key={label}>
          <dt>{label}</dt>
          <dd>
            {label === "Website" && val
              ? <a href={/^https?:/i.test(val) ? val : `https://${val}`} target="_blank" rel="noreferrer">{val}</a>
              : (val || "-")}
          </dd>
        </div>
      ) : null)}
      {p.aboutSource && (
        <div style={{ gridColumn: "1 / -1" }}>
          <dt>Description source</dt>
          <dd><a href={p.aboutUrl} target="_blank" rel="noreferrer">{p.aboutSource}</a> (CC BY-SA)</dd>
        </div>
      )}
    </dl>
  );
}
// ---- editable ratio grid (like Screener's "Edit ratios") ------------------------------
const RATIO_CATALOG = [
  ["price", "Current price", "$"], ["change_1d", "1 day change", "%"], ["ret_1y", "1 year return", "%"],
  ["high_52w", "52W high", "$"], ["low_52w", "52W low", "$"], ["pct_off_high", "% from 52W high", "%"],
  ["sma50", "50-day average", "$"], ["sma200", "200-day average", "$"], ["rsi14", "RSI (14)", ""],
  ["avg_vol", "Average volume (3M)", ""], ["vol_ratio", "Volume vs average", "x"],
  ["mcap", "Market cap", "$B"], ["pe", "Stock P/E", "x"], ["pe_avg10", "P/E on 10Y average profit", "x"],
  ["ps", "Price / sales", "x"], ["pb", "Price / book", "x"], ["ev_ebitda", "EV / EBITDA", "x"],
  ["earnings_yield", "Earnings yield (EBIT/EV)", "%"], ["fcf_yield", "FCF yield", "%"], ["div_yield", "Dividend yield", "%"], ["peg", "PEG", "x"],
  ["roce", "ROCE", "%"], ["roce_avg_3y", "ROCE, 3Y average", "%"], ["roe", "ROE", "%"], ["roa", "ROA", "%"], ["roic", "ROIC", "%"],
  ["gm", "Gross margin", "%"], ["opm", "Operating margin", "%"], ["nim", "Net margin", "%"],
  ["sales", "Sales (LTM)", "$M"], ["np", "Net profit (LTM)", "$M"], ["sales_g", "Sales growth (LTM YoY)", "%"], ["profit_g", "Profit growth (LTM YoY)", "%"],
  ["sales_cagr_3y", "Sales CAGR 3Y", "%"], ["sales_cagr_5y", "Sales CAGR 5Y", "%"], ["profit_cagr_3y", "Profit CAGR 3Y", "%"], ["profit_cagr_5y", "Profit CAGR 5Y", "%"],
  ["q_sales", "Sales (latest quarter)", "$M"], ["q_sales_g", "Quarter sales growth YoY", "%"], ["q_np", "Net profit (latest quarter)", "$M"], ["q_np_g", "Quarter profit growth YoY", "%"],
  ["debt_equity", "Debt / equity", "x"], ["current_ratio", "Current ratio", "x"], ["interest_cover", "Interest coverage", "x"],
  ["debt_chg_3y", "Debt change, 3Y", "%"], ["capex_sales", "Capex / sales", "%"], ["shares_chg_3y", "Share count change, 3Y", "%"],
  ["piotroski", "Piotroski F-score", ""], ["magic_rank", "Magic Formula rank", ""],
].map(([key, label, unit]) => ({ key, label, unit }));
const DEFAULT_RATIOS = ["mcap", "price", "high_52w", "low_52w", "pe", "pb", "div_yield", "roce", "roe", "sales", "nim", "debt_equity"];

function fmtRatio(v, unit) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return "-";
  const n = Number(v);
  const c = (x, d = 2) => x.toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
  switch (unit) {
    case "$": return `$${c(n)}`;
    case "%": return `${c(n, 1)}%`;
    case "$B": return n >= 1000 ? `$${c(n / 1000)} T` : `$${c(n)} B`;
    case "$M": return Math.abs(n) >= 1000 ? `$${c(n / 1000)} B` : `$${c(n, 0)} M`;
    case "x": return `${c(n)}x`;
    default: return Math.abs(n) >= 1000 ? c(n, 0) : c(n, 2);
  }
}

function Ratios({ ticker, snapshot }) {
  const loggedIn = !!getToken();
  const [saved, setSaved] = useState(() => loadUserData("ratios", null, []));
  const [draft, setDraft] = useState(null); // null = not editing
  const [q, setQ] = useState("");
  const [row, setRow] = useState(null);

  useEffect(() => {
    let dead = false;
    setRow(null);
    getValuationData(ticker)
      .then((d) => !dead && setRow(d && d.self ? d.self : {}))
      .catch(() => !dead && setRow({}));
    return () => { dead = true; };
  }, [ticker]);

  const list = draft || (saved && saved.length ? saved : null);
  const find = (k) => RATIO_CATALOG.find((r) => r.key === k);
  const term = q.trim().toLowerCase();
  const matches = term && draft
    ? RATIO_CATALOG.filter((r) => !draft.includes(r.key) && (r.label.toLowerCase().includes(term) || r.key.includes(term.replace(/\s+/g, "_")))).slice(0, 8)
    : [];

  const startEdit = () => setDraft(saved && saved.length ? [...saved] : [...DEFAULT_RATIOS]);
  const add = (k) => { if (draft.length < 24) setDraft([...draft, k]); setQ(""); };
  const save = () => { saveUserData("ratios", null, draft); setSaved(draft); setDraft(null); setQ(""); };
  const reset = () => { saveUserData("ratios", null, []); setSaved([]); setDraft(null); setQ(""); };
  const link = { background: "none", border: 0, color: "#6f42c1", letterSpacing: "0.15em", fontWeight: 600, textTransform: "uppercase", fontSize: "0.85rem", padding: "0 0 0 1rem" };

  return (
    <div className="co-ratios">
      <div className="co-snapshot">
        {list
          ? list.map((k) => {
              const r = find(k);
              if (!r) return null;
              return (
                <div key={k}>
                  <span>{r.label}</span>
                  <b>{row ? fmtRatio(row[k], r.unit) : "..."}</b>
                  {draft && <button type="button" aria-label={`Remove ${r.label}`} onClick={() => setDraft(draft.filter((x) => x !== k))} style={{ border: 0, background: "none", color: "#c0392b", fontSize: "1.1rem", lineHeight: 1, padding: "0 0 0 8px" }}>&times;</button>}
                </div>
              );
            })
          : (snapshot || []).map(([label, text]) => (
              <div key={label}><span>{label}</span><b>{text}</b></div>
            ))}
      </div>

      {loggedIn && (
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: "1rem", marginTop: "1rem" }}>
          {draft ? (
            <div style={{ flex: 1, position: "relative", maxWidth: 520 }}>
              <div style={{ fontWeight: 600, marginBottom: 6, color: "#000" }}>Add ratio to table</div>
              <input
                className="form-control" placeholder="eg. Debt / equity" value={q} autoComplete="off"
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && matches[0]) add(matches[0].key); }}
              />
              {matches.length > 0 && (
                <ul style={{ position: "absolute", left: 0, right: 0, top: "100%", zIndex: 50, margin: "2px 0 0", padding: 0, listStyle: "none", background: "#fff", color: "#000", border: "1px solid #ddd", borderRadius: 6, boxShadow: "0 6px 20px rgba(0,0,0,.15)" }}>
                  {matches.map((r) => (
                    <li key={r.key}>
                      <button type="button" onMouseDown={(e) => { e.preventDefault(); add(r.key); }} style={{ display: "block", width: "100%", textAlign: "left", border: 0, background: "transparent", color: "#000", padding: "8px 12px" }}>{r.label}</button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : <span />}
          <div style={{ whiteSpace: "nowrap" }}>
            {draft ? (
              <>
                <button type="button" style={link} onClick={save}>Save</button>
                <button type="button" style={{ ...link, color: "#555" }} onClick={() => { setDraft(null); setQ(""); }}>Cancel</button>
                <button type="button" style={{ ...link, color: "#c0392b" }} onClick={reset}>Reset</button>
              </>
            ) : (
              <button type="button" style={link} onClick={startEdit}>Edit ratios</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Holders(p) { return <ShareholdingPattern ticker={p.ticker || p.symbol || (p.stock && p.stock.ticker)} />; }
